import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { sectionSlugs, normalizeLang } from '@/mocks/sections';

type Article = {
  slug: string;
  title: string;
  description: string;
  excerpt: string;
  image: string;
  published: string;
  updated: string;
  author: string;
  readTime: number;
  tags: string[];
};

const MAIN_GUIDE_SLUGS = [
  'cssbuy-spreadsheet-guia-completo',
  'cssbuy-spreadsheet-complete-guide',
  'cssbuy-spreadsheet-kompletter-leitfaden',
];

export default function ArticlePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const langArticles = articles[lang] || articles.pt;
  const article: Article = langArticles.find((a) => a.slug === slug) || langArticles[0];
  const isMainGuide = MAIN_GUIDE_SLUGS.includes(article.slug);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article className="w-full">
        {/* Header Image */}
        <div className="w-full h-[320px] md:h-[420px] relative overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 lg:px-8 pb-8 md:pb-10">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {article.tags.slice(0, 3).map((tag, i) => (
                  <span
                    key={tag}
                    className={`px-2.5 py-1 text-xs font-medium text-white rounded-md ${i === 0 ? 'bg-accent-500' : 'bg-primary-500'}`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </div>

        {/* Meta Info */}
        <div className="w-full px-4 md:px-6 lg:px-8 py-4 bg-white border-b border-background-200/60">
          <div className="max-w-4xl mx-auto flex flex-wrap items-center gap-3 text-sm text-foreground-600">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
              {t('articles.published')}: {article.published}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
              {t('articles.updated')}: {article.updated}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
              {t('articles.author')}: {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
              {article.readTime} {t('articles.minRead')}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            {isMainGuide ? (
              <MainGuideContent />
            ) : (
              <GenericContent article={article} />
            )}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}

function GenericContent({ article }: { article: Article }) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const safetyHref = `/artigo/${sectionSlugs.safety[lang]}`;
  const guidesHref = `/artigo/${sectionSlugs.guides[lang]}`;

  return (
    <div className="prose prose-lg max-w-none">
      <p className="text-lg md:text-xl text-foreground-700 leading-relaxed mb-6">
        {article.excerpt}
      </p>

      <div className="bg-accent-50 rounded-xl p-5 mb-8 border border-accent-200/60 not-prose">
        <p className="text-sm text-foreground-700">
          <i className="ri-time-line w-4 h-4 inline-flex items-center justify-center text-accent-500 mr-1"></i>
          <strong>{t('articles.fullContentSoon')}</strong>
        </p>
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
        {t('articles.keyTopics')}
      </h2>
      <div className="flex flex-wrap gap-2 mb-8">
        {article.tags.map((tag) => (
          <span
            key={tag}
            className="px-3 py-1.5 text-sm font-medium bg-secondary-100 text-secondary-900 rounded-full"
          >
            {tag}
          </span>
        ))}
      </div>

      <p className="text-foreground-700 leading-relaxed mb-6">
        {article.description}
      </p>

      <div className="bg-background-100 rounded-xl p-6 mb-8 border border-background-200/60 not-prose">
        <h3 className="font-semibold text-foreground-950 mb-3">{t('articles.relatedArticles')}</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/" className="text-sm font-medium text-primary-500 hover:text-primary-600">
            <i className="ri-home-4-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
            {t('articles.backToHome')}
          </Link>
          <Link to={safetyHref} className="text-sm font-medium text-primary-500 hover:text-primary-600">
            <i className="ri-shield-star-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
            {t('nav.safety')}
          </Link>
          <Link to={guidesHref} className="text-sm font-medium text-primary-500 hover:text-primary-600">
            <i className="ri-book-open-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
            {t('nav.guides')}
          </Link>
        </div>
      </div>

      <div className="bg-primary-50 rounded-xl p-8 text-center border border-primary-200/60 not-prose">
        <h3 className="text-xl font-bold text-foreground-950 mb-3">
          {t('finalCta.h2')}
        </h3>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-5">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <span>{t('button.cssbuySpreadsheet')}</span>
            <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
          </a>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-primary-500 text-primary-500 font-semibold rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
          >
            <span>{t('articles.backToHome')}</span>
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}

function MainGuideContent() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const safetyHref = `/artigo/${sectionSlugs.safety[lang]}`;
  const comparisonsHref = `/artigo/${sectionSlugs.comparisons[lang]}`;

  return (
    <div className="prose prose-lg max-w-none">
      {/* Intro */}
      <p className="text-lg md:text-xl text-foreground-700 leading-relaxed mb-8">
        O <strong>CSSBuy Spreadsheet</strong> é uma ferramenta essencial para quem busca organizar, pesquisar e comparar produtos de forma eficiente. Este guia independente cobre tudo o que você precisa saber — desde a estrutura básica até estratégias avançadas para encontrar produtos, avaliar vendedores e tomar decisões de compra seguras.
      </p>

      {/* TOC */}
      <div className="bg-background-100 rounded-xl p-6 mb-10 border border-background-200/60 not-prose">
        <h2 className="text-lg font-semibold text-foreground-950 mb-4">Índice</h2>
        <ul className="space-y-2 text-sm">
          <li><a href="#o-que-e" className="text-primary-500 hover:text-primary-600 transition-colors">O que é CSSBuy Spreadsheet?</a></li>
          <li><a href="#como-funciona" className="text-primary-500 hover:text-primary-600 transition-colors">Como funciona um CSSBuy Spreadsheet?</a></li>
          <li><a href="#categorias" className="text-primary-500 hover:text-primary-600 transition-colors">Principais Categorias de Produtos</a></li>
          <li><a href="#vendedores" className="text-primary-500 hover:text-primary-600 transition-colors">Como Avaliar Vendedores</a></li>
          <li><a href="#qc" className="text-primary-500 hover:text-primary-600 transition-colors">Entendendo QC (Quality Control)</a></li>
          <li><a href="#envio" className="text-primary-500 hover:text-primary-600 transition-colors">Custos de Envio e Métodos</a></li>
          <li><a href="#seguranca" className="text-primary-500 hover:text-primary-600 transition-colors">Segurança na Compra</a></li>
          <li><a href="#comparacao" className="text-primary-500 hover:text-primary-600 transition-colors">Comparação: CSSBuy vs Outros</a></li>
          <li><a href="#faq" className="text-primary-500 hover:text-primary-600 transition-colors">Perguntas Frequentes</a></li>
        </ul>
      </div>

      {/* Section 1 */}
      <h2 id="o-que-e" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">
        O que é CSSBuy Spreadsheet?
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        O <strong>CSSBuy Spreadsheet</strong> é um recurso organizado que reúne informações sobre produtos, vendedores, preços, links e dados relevantes para compras internacionais. Diferente de listas simples, um spreadsheet bem estruturado permite filtrar, comparar e encontrar rapidamente o que você procura — economizando tempo e reduzindo riscos.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-4">
        No contexto do <strong>CSSBuy</strong>, um agente de compras popular, o spreadsheet funciona como um diretório independente de recursos. Ele não substitui o site oficial, mas complementa a experiência de compra ao oferecer uma visão organizada das opções disponíveis.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-6">
        Este site, <strong>CSSBuyBR.INK</strong>, é um <strong>recurso independente</strong>. Não somos o site oficial do CSSBuy. Nosso objetivo é organizar informações úteis, guias e comparativos para que você tome decisões mais informadas antes de comprar.
      </p>

      {/* Internal Link */}
      <div className="bg-primary-50 rounded-xl p-5 mb-8 border border-primary-200/60 not-prose">
        <p className="text-sm text-foreground-700">
          <i className="ri-lightbulb-line w-4 h-4 inline-flex items-center justify-center text-primary-500 mr-1"></i>
          <strong>Dica:</strong> Antes de usar qualquer spreadsheet, verifique se as informações estão atualizadas. Links desatualizados podem levar a produtos esgotados ou preços incorretos. Confira sempre nosso <Link to={safetyHref} className="text-primary-500 font-semibold hover:text-primary-600">Guia de Segurança</Link> para dicas de verificação.
        </p>
      </div>

      {/* Section 2 */}
      <h2 id="como-funciona" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">
        Como funciona um CSSBuy Spreadsheet?
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        Um spreadsheet funciona como uma planilha organizada onde cada linha representa um produto, vendedor ou recurso. As colunas geralmente incluem: nome do produto, categoria, link do vendedor, preço aproximado, notas de qualidade, data de atualização e status de disponibilidade.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-6">
        A principal vantagem é a <strong>organização por categorias</strong>. Em vez de navegar por centenas de páginas diferentes, você pode filtrar por Shoes, Hoodies, T-Shirts, Jackets e outras categorias — encontrando exatamente o que precisa em segundos.
      </p>

      {/* Steps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 not-prose">
        {[
          { step: '1', title: 'Pesquise', desc: 'Use filtros de categoria ou a barra de busca para encontrar produtos específicos.' },
          { step: '2', title: 'Analise', desc: 'Verifique informações do vendedor, preços, disponibilidade e notas de qualidade.' },
          { step: '3', title: 'Compare', desc: 'Avalie diferentes opções lado a lado antes de tomar uma decisão.' },
        ].map((item) => (
          <div key={item.step} className="bg-white rounded-xl p-5 border border-background-200/60">
            <div className="w-10 h-10 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mb-3">
              {item.step}
            </div>
            <h3 className="font-semibold text-foreground-950 mb-1">{item.title}</h3>
            <p className="text-sm text-foreground-600">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* Section 3 */}
      <h2 id="categorias" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">
        Principais Categorias de Produtos
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        O CSSBuy Spreadsheet abrange uma ampla variedade de categorias. Aqui estão as principais e o que esperar de cada uma:
      </p>

      <div className="overflow-x-auto mb-6">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-background-100">
              <th className="text-left px-4 py-3 font-semibold text-foreground-950 border border-background-200/60">Categoria</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-950 border border-background-200/60">Descrição</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-950 border border-background-200/60">O que Verificar</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-3 border border-background-200/60 font-medium">Shoes</td>
              <td className="px-4 py-3 border border-background-200/60 text-foreground-600">Tênis, sneakers e calçados em geral</td>
              <td className="px-4 py-3 border border-background-200/60 text-foreground-600">Tamanho, modelo, material, QC</td>
            </tr>
            <tr className="bg-background-50/50">
              <td className="px-4 py-3 border border-background-200/60 font-medium">Hoodies/Sweaters</td>
              <td className="px-4 py-3 border border-background-200/60 text-foreground-600">Moletom, casacos e suéteres</td>
              <td className="px-4 py-3 border border-background-200/60 text-foreground-600">Tecido, caimento, bordados</td>
            </tr>
            <tr>
              <td className="px-4 py-3 border border-background-200/60 font-medium">T-Shirts</td>
              <td className="px-4 py-3 border border-background-200/60 text-foreground-600">Camisetas de todas as marcas</td>
              <td className="px-4 py-3 border border-background-200/60 text-foreground-600">Estampa, tecido, tamanho</td>
            </tr>
            <tr className="bg-background-50/50">
              <td className="px-4 py-3 border border-background-200/60 font-medium">Jackets</td>
              <td className="px-4 py-3 border border-background-200/60 text-foreground-600">Jaquetas e corta-vento</td>
              <td className="px-4 py-3 border border-background-200/60 text-foreground-600">Material, costura, detalhes</td>
            </tr>
            <tr>
              <td className="px-4 py-3 border border-background-200/60 font-medium">Accessories</td>
              <td className="px-4 py-3 border border-background-200/60 text-foreground-600">Acessórios variados</td>
              <td className="px-4 py-3 border border-background-200/60 text-foreground-600">Qualidade, material, acabamento</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-foreground-600 text-sm italic mb-6">
        Nota: Informações podem variar — verifique dados atuais antes de comprar. Sempre consulte fontes oficiais quando disponíveis.
      </p>

      {/* Internal Link to Home Categories */}
      <p className="text-foreground-700 leading-relaxed mb-8">
        Explore todas as categorias disponíveis na nossa <Link to="/" className="text-primary-500 font-semibold hover:text-primary-600">página inicial</Link>, onde você encontra links diretos para cada categoria de produtos no spreadsheet principal.
      </p>

      {/* Section 4 */}
      <h2 id="vendedores" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">
        Como Avaliar Vendedores
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        Avaliar vendedores é uma das etapas mais importantes antes de comprar. Um vendedor confiável pode fazer toda a diferença na qualidade do produto e na experiência de compra. Aqui estão os fatores-chave para considerar:
      </p>
      <ul className="list-disc list-inside space-y-2 text-foreground-700 mb-6">
        <li><strong>Tempo de atividade:</strong> Vendedores com mais tempo de mercado geralmente têm mais feedback disponível.</li>
        <li><strong>Variedade de produtos:</strong> Vendedores especializados em uma categoria tendem a ter melhor qualidade.</li>
        <li><strong>Comunicação:</strong> A capacidade de responder perguntas é um bom indicador de profissionalismo.</li>
        <li><strong>Política de reenvio:</strong> Verifique se o vendedor oferece reenvio em caso de defeitos.</li>
        <li><strong>Preços:</strong> Preços muito abaixo da média podem ser um alerta — verifique sempre.</li>
      </ul>

      {/* Section 5 */}
      <h2 id="qc" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">
        Entendendo QC (Quality Control)
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        O <strong>Quality Control (QC)</strong> é o processo de verificação de qualidade onde fotos detalhadas do produto são tiradas antes do envio. Essas fotos permitem que você confirme se o produto corresponde ao que foi pedido.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-4">
        Ao analisar fotos de QC, preste atenção em: costura, alinhamento de estampas, cores, materiais, etiquetas, e qualquer detalhe que possa indicar diferenças em relação ao produto anunciado. Se algo parecer incorreto, é melhor solicitar esclarecimentos antes de aprovar o envio.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-6">
        O QC não é garantia de perfeição, mas é uma camada importante de proteção. Combine QC com pesquisa de vendedores para uma experiência de compra mais segura.
      </p>

      {/* Section 6 */}
      <h2 id="envio" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">
        Custos de Envio e Métodos
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        O custo de envio é um dos fatores mais variáveis no processo de compra internacional. Depende do peso, dimensões, destino e método escolhido. O CSSBuy oferece várias opções de envio, cada uma com seus prós e contras.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-4">
        Para o <strong>Brasil</strong>, métodos como EMS, DHL e ePacket são comuns, mas prazos e custos variam. Para a <strong>Alemanha</strong> e Europa, DHL e tax-free lines são frequentemente usados. Sempre calcule o custo total incluindo taxas de serviço do agente antes de finalizar a compra.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-6">
        Nosso <strong>guia de envio detalhado</strong> cobre estimativas, comparações de métodos e dicas para reduzir custos. Consulte a seção de envio para informações atualizadas.
      </p>

      {/* Section 7 */}
      <h2 id="seguranca" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">
        Segurança na Compra
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        A segurança deve ser sempre prioridade. Aqui estão princípios fundamentais para compras seguras usando recursos de spreadsheet:
      </p>
      <ul className="list-disc list-inside space-y-2 text-foreground-700 mb-6">
        <li>Verifique sempre links antes de clicar — phishing é uma ameaça real.</li>
        <li>Nunca compartilhe informações de login ou dados de pagamento em grupos não oficiais.</li>
        <li>Confirme que está no site correto antes de inserir qualquer dado sensível.</li>
        <li>Use QC como ferramenta de verificação, não como única garantia.</li>
        <li>Desconfie de descontos que parecem excessivos ou urgentes.</li>
        <li>Guarde registros de todas as transações e comunicações.</li>
      </ul>

      <div className="bg-accent-50 rounded-xl p-5 mb-8 border border-accent-200/60 not-prose">
        <p className="text-sm text-foreground-700">
          <i className="ri-shield-check-line w-4 h-4 inline-flex items-center justify-center text-accent-500 mr-1"></i>
          <strong>Importante:</strong> Este site (CSSBuyBR.INK) é um recurso independente de informações. Não somos o site oficial do CSSBuy. Sempre verifique informações importantes em fontes oficiais antes de comprar. Consulte nossa <Link to={safetyHref} className="text-accent-500 font-semibold hover:text-accent-600">Central de Segurança</Link> para mais dicas.
        </p>
      </div>

      {/* Section 8 */}
      <h2 id="comparacao" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">
        Comparação: CSSBuy vs Outros Agentes
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        O CSSBuy é um dos vários agentes de compra disponíveis. Cada um tem características distintas em termos de taxas, velocidade de processamento, opções de envio e interface. A escolha do agente certo depende das suas prioridades: custo, velocidade, facilidade de uso ou variedade de opções.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-6">
        Nossos <strong>guias de comparação</strong> analisam CSSBuy vs Superbuy, Sugargoo, AllChinaBuy e outros agentes populares — com foco em fatores que realmente importam para compradores. Confira a seção de <Link to={comparisonsHref} className="text-primary-500 font-semibold hover:text-primary-600">comparações</Link> para análises detalhadas.
      </p>

      {/* FAQ Section */}
      <h2 id="faq" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">
        Perguntas Frequentes (FAQ)
      </h2>

      <div className="space-y-4 mb-10">
        {[
          { q: 'O CSSBuy Spreadsheet é oficial?', a: 'Não. Os spreadsheets são recursos criados pela comunidade. Este site (CSSBuyBR.INK) também é independente e não afiliado oficialmente ao CSSBuy.' },
          { q: 'Como sei se um vendedor é confiável?', a: 'Verifique tempo de atividade, feedback de outros compradores, qualidade das fotos de produto e capacidade de comunicação. Sempre combine com QC para verificação visual.' },
          { q: 'O que fazer se o produto no QC não corresponde ao esperado?', a: 'Entre em contato com o vendedor através do agente. A maioria dos agentes permite recusar o item antes do envio, desde que dentro do prazo.' },
          { q: 'Quanto tempo leva o envio para o Brasil?', a: 'Depende do método. EMS geralmente leva 15–30 dias, DHL pode ser mais rápido mas mais caro. Epacket é econômico mas mais lento. Fatores como alfândega também influenciam.' },
          { q: 'É seguro usar links de spreadsheet?', a: 'Links devem ser verificados antes de uso. Confira o domínio, avaliações do vendedor e use sempre o processo oficial do agente para compras. Nunca pague diretamente a vendedores desconhecidos.' },
        ].map((faq, idx) => (
          <div key={idx} className="bg-white rounded-xl p-5 border border-background-200/60">
            <h3 className="font-semibold text-foreground-950 mb-2">{faq.q}</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>

      {/* Final CTA */}
      <div className="bg-primary-50 rounded-xl p-8 text-center border border-primary-200/60 not-prose">
        <h3 className="text-xl font-bold text-foreground-950 mb-3">
          Pronto para explorar o CSSBuy Spreadsheet?
        </h3>
        <p className="text-foreground-600 mb-6 max-w-xl mx-auto">
          Encontre produtos, compare vendedores e acesse guias detalhados para uma experiência de compra mais informada e segura.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <span>{t('button.cssbuySpreadsheet')}</span>
            <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
          </a>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-primary-500 text-primary-500 font-semibold rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
          >
            <span>{t('articles.backToHome')}</span>
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}